import { Badge, Box, useToast } from '@razorpay/blade/components';
import IsFeatureDisabledForRearch from 'access/IsFeatureDisabledForRearch';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import api from 'api';
import { WorkflowTypesMap } from 'components/ApprovalWorkflows/constants';
import ExpansionPanel from 'components/ui/Accordion/ExpansionPanel';
import { ExpansionList } from 'components/ui/Accordion/ExpansionPanel/ExpansionList';
import useExpansionPanel from 'components/ui/Accordion/ExpansionPanel/useExpansionPanel';
import { WarningAlert } from 'components/ui/Alert';
import { StandardButton, StandardSecondaryButton } from 'components/ui/Button';
import { useModal } from 'components/ui/Modals';
import Modal from 'components/ui/Modals/components/Modal';
import { getDaysInMonth } from 'date-fns';
import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { trackXPayrollEvent } from 'utils/analytics';
import { DECIMAL_REGEX } from 'utils/regexes';
import loggedInUserSelectors from '../../../reducers/loggedInUser/selectors';
import { SetErrorMessageType } from '../contexts/RunPayrollErrorContext';
import { FETCH_RUNPAYROLL_DATA } from '../utils/queries';
import { APPROVAL_DATA_FETCH } from '../utils/useApprovalLink';
import { RunPayrollUpdateSchemaContract } from 'schemas/RunPayrollUpdateSchema';
import { UpdatePayrollRearchWithWorkflowSchemaContract } from 'api/types/runPayrollTypes';
import { DeductionComponentV2, RunPayrollData } from '../utils/types';
import useNewJoinerArrearEnabled from '../utils/useNewJoinerArrearEnabled';
import { PayrollBulkActions, Remarks, ArrearsV2, Overtime } from './EditSalary/components';
import AdditionsV2 from './EditSalary/components/AdditionsV2';
import DeductionsV2 from './EditSalary/components/DeductionsV2';
import useAdditionsDeductionsLop from './EditSalary/hooks/useAdditionsDeductionsLop';
import { useOvertimeMutations } from './EditSalary/components/Overtime/hooks/useOvertimeMutations';
import type { OvertimeRef } from './EditSalary/components/Overtime/types';
import { localOtStateToApiPayload } from './EditSalary/components/Overtime/utils';
import { convertDataToUpdateAdditionsDeductionsPayload } from './EditSalary/utils';
import { RunPayrollModalHeader } from './components/RunPayrollModalHeader';
import styles from './index.module.scss';
import queryKeys from './EditSalary/components/queries';
import type { DeferredArrears } from './EditSalary/components/ArrearsV2/types';
import type { UpdateOvertimeEntriesInput } from 'api/gql-utils/overtime/types';

export type OvertimeSnapshot = UpdateOvertimeEntriesInput;

export const toOvertimeMutationInput = ({
  userId,
  payrollMonth,
  entries,
}: OvertimeSnapshot): UpdateOvertimeEntriesInput => ({ userId, payrollMonth, entries });

const EditSalaryV2 = ({
  payrollEntry,
  onError,
  payrollMonth,
}: { payrollEntry: RunPayrollData; payrollMonth: string } & SetErrorMessageType) => {
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { newJoinerArrearPaymentMonthExists } = useNewJoinerArrearEnabled(payrollEntry);
  const { isM3S1Enabled } = useIsFeatureDisabledForRearch();

  const features = useSelector(loggedInUserSelectors.features);
  const actionsWithActiveWorkflows = useSelector(loggedInUserSelectors.actionsWithActiveWorkflows);
  const doesEditNeedApproval = actionsWithActiveWorkflows.includes(WorkflowTypesMap.EDIT_PAYROLL);

  const isRearchApprovalWorkflowEnabled = Boolean(features?.['reArchApprovalWorkflow']);
  const useDeferredApprovalSubmit = doesEditNeedApproval && isRearchApprovalWorkflowEnabled;

  const isOvertimeEnabled = Boolean(features?.overtime);

  const overtimeRef = useRef<OvertimeRef>(null);
  const { saveEntries } = useOvertimeMutations();

  // Lightweight query just for the badge — shares cache with the Overtime component's query
  const formulaCardsQuery = useQuery({
    queryKey: [queryKeys.OVERTIME_FORMULA_CARDS_KEY],
    queryFn: () => api.overtime.queries.listFormulaCards({ status: 'active' }),
    enabled: isOvertimeEnabled,
    select: (response) => response.overtimeListFormulaCards.data.otFormulaCards,
  });
  const hasOTCards = (formulaCardsQuery.data?.length ?? 0) > 0;

  const overtimePanelExtra = isOvertimeEnabled ? 1 : 0;
  const totalNumberOfPanels =
    (isM3S1Enabled
      ? newJoinerArrearPaymentMonthExists
        ? 3
        : 4
      : newJoinerArrearPaymentMonthExists
      ? 2
      : 3) + overtimePanelExtra;

  const panelIdx = {
    deduction: newJoinerArrearPaymentMonthExists ? 0 : 1,
    overtime: isOvertimeEnabled ? (newJoinerArrearPaymentMonthExists ? 1 : 2) : null,
    arrears: (newJoinerArrearPaymentMonthExists ? 1 : 2) + overtimePanelExtra,
    remark:
      (isM3S1Enabled
        ? newJoinerArrearPaymentMonthExists
          ? 2
          : 3
        : newJoinerArrearPaymentMonthExists
        ? 1
        : 2) + overtimePanelExtra,
  };

  const expansionPanel = useExpansionPanel(totalNumberOfPanels);
  const toast = useToast();

  const {
    allowances,
    onAllowanceChange,
    onAllowanceAdd,
    onAllowanceDelete,
    isAdditionsDeductionsListLoading,
    deductions,
    setDeductions,
    errorMessage,
    updateAdditionsDeductionsLop,
    existingComponents,
    deletedVariableAllowances,
    deleteVariableAdditions,
  } = useAdditionsDeductionsLop({
    payrollEntry,
    payrollMonth,
    onUpdateSuccess: () => {
      // Only close if overtime mutation isn't pending — handled in onSubmit
    },
  });

  const setNonLOPDeductions = (nonLOPDeductions: DeductionComponentV2['non_lop_deductions']) => {
    setDeductions({
      ...deductions,
      non_lop_deductions: nonLOPDeductions,
    });
  };

  const [remarks, setRemarks] = useState(payrollEntry.remarks);
  const [deferredArrears, setDeferredArrears] = useState<DeferredArrears | null>(null);

  const updateWithWorkflow = useMutation(
    (payload: UpdatePayrollRearchWithWorkflowSchemaContract) => {
      return api.runPayroll.updatePayrollRearchWithWorkflow(payload);
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries([FETCH_RUNPAYROLL_DATA]);
        if (response['hasChangeRequest']) {
          queryClient.invalidateQueries(APPROVAL_DATA_FETCH);
          toast.show({
            id: `edit-approval-${payrollEntry.id}`,
            content: 'Approval Request Sent!',
            color: 'positive',
          });
        } else {
          toast.show({
            id: String(payrollEntry.id),
            content: 'Payroll data updated successfully',
            color: 'positive',
          });
        }
        closeModal();
      },
      onError: (error) => {
        toast.show({
          id: `edit-salary-error-${payrollEntry.id}`,
          content:
            error instanceof Error && error.message
              ? error.message
              : 'Failed to submit the edit — please try again.',
          color: 'negative',
        });
      },
    },
  );

  const skip = useMutation(
    ({ payrollId }: { payrollId: number }) => {
      return api.runpayrollMutations.bulkSkipPayroll({
        payroll_month: payrollMonth,
        ids: [payrollId],
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FETCH_RUNPAYROLL_DATA);
        closeModal();
      },
      onError: (error) => {
        onError && onError({ message: (error as Error).message });
        closeModal();
      },
    },
  );

  const skipAllExceptThis = useMutation(
    ({ payrollId }: { payrollId: number }) => {
      return api.runpayrollMutations.skipAllExceptThis({
        payroll_month: payrollMonth,
        payroll_id: payrollId,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FETCH_RUNPAYROLL_DATA);
        closeModal();
      },
      onError: (error) => {
        onError && onError({ message: (error as Error).message });
        closeModal();
      },
    },
  );

  const onLOPDaysChange = (value: string) => {
    if (!value.match(DECIMAL_REGEX)) {
      return;
    }

    const daysInCurrentMonth = getDaysInMonth(new Date(payrollMonth));

    if (parseFloat(value) > daysInCurrentMonth) {
      return;
    }

    setDeductions({
      ...deductions,
      lop_deduction: {
        ...deductions.lop_deduction,
        lop_days: value ?? '0',
      },
    });
  };

  const isActionLoading =
    skip.isLoading ||
    skipAllExceptThis.isLoading ||
    isAdditionsDeductionsListLoading ||
    saveEntries.isLoading;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    trackXPayrollEvent(`edit_salary.DONE`);

    // Validate overtime entries if enabled
    if (isOvertimeEnabled && overtimeRef.current) {
      const otEntries = overtimeRef.current.getEntries();
      const hasOTEntries = otEntries.some((e) => e.otFormulaCardId);
      if (hasOTEntries && !overtimeRef.current.validate()) {
        return;
      }
    }

    const payrollStagingId = payrollEntry.payrollStagingId;
    if (useDeferredApprovalSubmit && payrollStagingId == null) {
      toast.show({
        id: `edit-salary-error-${payrollEntry.id}`,
        content: 'Could not submit for approval — please reload the page and try again.',
        color: 'negative',
      });
      return;
    }

    const getOvertimeSnapshot = (): OvertimeSnapshot | null => {
      if (!isOvertimeEnabled || !overtimeRef.current) return null;
      if (!overtimeRef.current.isSeeded()) return null;

      const otEntries = overtimeRef.current.getEntries();
      const validEntries = otEntries.filter((e) => e.otFormulaCardId);
      if (
        validEntries.length === 0 &&
        otEntries.length === 0 &&
        !overtimeRef.current.hadExistingEntries()
      )
        return null;

      const otBaseDays = overtimeRef.current.getBaseDays();
      const entries = validEntries.map((e) => localOtStateToApiPayload(e, otBaseDays));

      const seeded = overtimeRef.current.getSeededEntries();
      const seededPayload = seeded.map((e) => localOtStateToApiPayload(e, otBaseDays));
      if (JSON.stringify(entries) === JSON.stringify(seededPayload)) {
        return null;
      }

      return {
        userId: String(payrollEntry.peopleId),
        payrollMonth,
        entries,
      };
    };

    const overtimeSnapshot = getOvertimeSnapshot();

    if (useDeferredApprovalSubmit) {
      if (payrollStagingId == null) {
        return;
      }

      const isComputeManaged = (additionalSdiData: unknown) => {
        const deductionType = (additionalSdiData as { deduction_type?: string } | undefined)
          ?.deduction_type;
        return deductionType === 'recurring' || deductionType === 'adhoc_payment';
      };
      const changedManagedRows = deductions.non_lop_deductions.filter((deduction) => {
        if (!isComputeManaged(deduction.additionalSdiData)) return false;
        const original = existingComponents?.deductions?.find((e) => e.name === deduction.name);
        return original !== undefined && Number(deduction.amount) !== Number(original.amount);
      });
      if (changedManagedRows.length > 0) {
        toast.show({
          id: `edit-salary-error-${payrollEntry.id}`,
          content: `Changes to ${changedManagedRows
            .map((d) => d.name)
            .join(
              ', ',
            )} cannot go through an approval request — recurring and adhoc-payment deductions are managed separately. Restore those amounts, then submit your other changes.`,
          color: 'negative',
        });
        return;
      }

      const assignmentPayload = convertDataToUpdateAdditionsDeductionsPayload({
        allowances,
        deductions,
        userId: String(payrollEntry.peopleId),
        payrollMonth,
        existingComponents,
        isM3S1Enabled,
      });

      updateWithWorkflow.mutate({
        overtime_entries: overtimeSnapshot,
        payroll_id: payrollStagingId,
        component_assignments: {
          addition: assignmentPayload.additions.filter((a) => a.payType !== 'variable'),
          variablePay: assignmentPayload.additions.filter((a) => a.payType === 'variable'),
          deduction: assignmentPayload.deductions,
          deletedAdditionIds: assignmentPayload.deletedAdditionIds,
          deletedDeductionIds: assignmentPayload.deletedDeductionIds,
          deletedVariablePayIds: deletedVariableAllowances
            .map((a) => a.componentId)
            .filter((id): id is string => Boolean(id)),
          lop: Number(deductions.lop_deduction.lop_days ?? 0),
          effectiveMonth: assignmentPayload.effectiveFrom,
        },
        remarks: remarks ?? '',
        arrears: [] as RunPayrollUpdateSchemaContract['arrears'],
        rearch_arrears_request: deferredArrears?.request ?? null,
      });
      return;
    }

    // when approval workflow is not enabled
    if (deletedVariableAllowances.length > 0) {
      deleteVariableAdditions.mutate(deletedVariableAllowances);
    }

    if (overtimeSnapshot) {
      try {
        await saveEntries.mutateAsync(toOvertimeMutationInput(overtimeSnapshot));
      } catch (error) {
        toast.show({
          id: `edit-salary-error-${payrollEntry.id}`,
          content: error instanceof Error ? error.message : 'Unknown error',
          color: 'negative',
        });
        return;
      }
    }

    try {
      await new Promise<void>((resolve, reject) => {
        updateAdditionsDeductionsLop.mutate(
          convertDataToUpdateAdditionsDeductionsPayload({
            allowances,
            deductions,
            userId: String(payrollEntry.peopleId),
            payrollMonth,
            existingComponents,
            isM3S1Enabled,
          }),
          {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          },
        );
      });
    } catch (error) {
      toast.show({
        id: `edit-salary-error-${payrollEntry.id}`,
        content: error instanceof Error ? error.message : 'Unknown error',
        color: 'negative',
      });
      return;
    }

    queryClient.invalidateQueries([FETCH_RUNPAYROLL_DATA]);
    closeModal();
    toast.show({
      id: String(payrollEntry.id),
      content: 'Payroll data updated successfully',
      color: 'positive',
    });
  };

  const onClose = () => {
    queryClient.invalidateQueries([queryKeys.QUERY_LIST_ADDITIONS_DEDUCTIONS_LOP]);
  };

  return (
    <Modal onClose={onClose}>
      <div className="max-h-screen overflow-y-auto">
        <div className={`flex flex-col max-w-xl ${styles['run-payroll-modal']}`}>
          <RunPayrollModalHeader>Edit Salary</RunPayrollModalHeader>
          {errorMessage && (
            <WarningAlert>
              <span>{errorMessage}</span>
            </WarningAlert>
          )}
          <form className={`${styles['run-payroll-modal-content']}`} onSubmit={onSubmit}>
            <ExpansionList className="mb-16">
              {!newJoinerArrearPaymentMonthExists && (
                <ExpansionPanel
                  {...expansionPanel.panelProps[0]}
                  onHeaderClick={expansionPanel.onHeaderClick}
                  header={'Addition'}
                  scrollable={true}
                  childrenClassName={styles['run-payroll-addition-expansion-child']}>
                  <AdditionsV2
                    allowances={allowances}
                    onAllowanceChange={onAllowanceChange}
                    payrollEntry={payrollEntry}
                    payrollMonth={payrollMonth}
                    onAllowanceAdd={onAllowanceAdd}
                    onAllowanceDelete={onAllowanceDelete}
                  />
                </ExpansionPanel>
              )}
              <IsFeatureDisabledForRearch feature={['isDeductionsDisabled']}>
                <ExpansionPanel
                  {...expansionPanel.panelProps[panelIdx.deduction]}
                  onHeaderClick={expansionPanel.onHeaderClick}
                  header={'Deduction'}
                  scrollable={true}
                  className="pt-0"
                  childrenClassName={styles['run-payroll-deduction-expansion-child']}>
                  <DeductionsV2
                    deductions={deductions}
                    onLOPDaysChange={onLOPDaysChange}
                    payrollEntry={payrollEntry}
                    setNonLOPDeductions={setNonLOPDeductions}
                    payrollMonth={payrollMonth}
                  />
                </ExpansionPanel>
              </IsFeatureDisabledForRearch>
              {isOvertimeEnabled && panelIdx.overtime !== null && (
                <ExpansionPanel
                  {...expansionPanel.panelProps[panelIdx.overtime]}
                  onHeaderClick={expansionPanel.onHeaderClick}
                  header={
                    <>
                      Overtime{' '}
                      {hasOTCards && (
                        <Badge size="small" color="primary">
                          NEW
                        </Badge>
                      )}
                    </>
                  }
                  scrollable={true}
                  childrenClassName={styles['run-payroll-overtime-expansion-child']}>
                  <Overtime
                    ref={overtimeRef}
                    payrollEntry={payrollEntry}
                    payrollMonth={payrollMonth}
                  />
                </ExpansionPanel>
              )}
              {isM3S1Enabled ? (
                <ExpansionPanel
                  {...expansionPanel.panelProps[panelIdx.arrears]}
                  onHeaderClick={expansionPanel.onHeaderClick}
                  header={'Arrears'}
                  scrollable={true}>
                  <ArrearsV2
                    payrollEntry={payrollEntry}
                    payrollMonth={payrollMonth}
                    onDeferCommit={useDeferredApprovalSubmit ? setDeferredArrears : undefined}
                    pendingRows={deferredArrears?.rows}
                  />
                </ExpansionPanel>
              ) : null}
              <IsFeatureDisabledForRearch feature={['isMileStone1Enabled', 'isM2S1Enabled']}>
                <ExpansionPanel
                  {...expansionPanel.panelProps[panelIdx.remark]}
                  onHeaderClick={expansionPanel.onHeaderClick}
                  header={'Remark'}>
                  <Remarks remarks={remarks} onRemarkChange={(value) => setRemarks(value)} />
                </ExpansionPanel>
              </IsFeatureDisabledForRearch>
            </ExpansionList>
            <div className="flex gap-[8px]">
              <StandardButton
                isPending={
                  updateAdditionsDeductionsLop.isLoading ||
                  saveEntries.isLoading ||
                  updateWithWorkflow.isLoading
                }
                disabled={isActionLoading}
                type={'submit'}
                className={doesEditNeedApproval ? styles['run-payroll-edit-model-button'] : ''}>
                {doesEditNeedApproval ? 'Request For Approval' : 'Done'}
              </StandardButton>
              {!newJoinerArrearPaymentMonthExists && (
                <>
                  <StandardSecondaryButton
                    onClick={() => {
                      trackXPayrollEvent(`edit_salary.skip_this`);
                      skip.mutate({ payrollId: payrollEntry.id });
                    }}
                    isPending={skip.isLoading}
                    disabled={isActionLoading}
                    className={doesEditNeedApproval ? styles['run-payroll-edit-model-button'] : ''}>
                    Skip
                  </StandardSecondaryButton>
                  <StandardSecondaryButton
                    onClick={() => {
                      trackXPayrollEvent(`edit_salary.skip_all_except_this`);
                      skipAllExceptThis.mutate({ payrollId: payrollEntry.id });
                    }}
                    isPending={skipAllExceptThis.isLoading}
                    disabled={isActionLoading}
                    className={doesEditNeedApproval ? styles['run-payroll-edit-model-button'] : ''}>
                    Skip All Except This
                  </StandardSecondaryButton>
                </>
              )}
            </div>
          </form>
        </div>
        <PayrollBulkActions payrollMonth={payrollMonth} />
      </div>
    </Modal>
  );
};

export default EditSalaryV2;
